import { useEffect, useRef, useCallback } from 'react';
import { Button, FormField } from '@chikara/ui';
import { useActivateCardForm } from './useActivateCardForm';
import { CARD_MAX_LENGTH, STEP_DATA, STEP_LIMIT, STEP_CONFIRMATION, STEP_FAILED } from './activateCard.constants';
import { lastFourDigits, formatCardNumber, formatCurrencyInput, hasDisplayFullName } from './activateCard.validators';
import { MfaModal } from '@/features/auth/components/MfaFlow/MfaModal';
import { ActivateCardModalProps } from './activateCard.types';
import styles from './ActivateCardModal.module.css';
import { t } from '@/i18n';


export function ActivateCardModal({
  isOpen,
  onClose,
  onSuccess,
}: ActivateCardModalProps) {
  const {
    state,
    handleFullNameChange,
    handleCardNumberChange,
    handleConfirmCardNumberChange,
    handleSpendingLimitChange,
    handleSpendingLimitFocus,
    handleSpendingLimitBlur,
    handleNext,
    handleVerifyMfa,
    handleClose,
    canGoNext,
    canActivate,
    isMfaOpen,
    setIsMfaOpen,
    openMfa,
    handleSuccess,
    isGlobalLoading,
  } = useActivateCardForm(onClose, isOpen, onSuccess);

  const isClickInsideRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    isClickInsideRef.current = contentRef.current?.contains(e.target as Node) ?? false;
  };

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (
        event.target === event.currentTarget &&
        !isClickInsideRef.current &&
        state.step !== STEP_CONFIRMATION &&
        !state.isLoading
      ) {
        handleClose();
      }
    },
    [handleClose, state.step, state.isLoading]
  );

  // Handle ESC key (disabled on step 3)
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && state.step !== STEP_CONFIRMATION) {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, state.step]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className={isGlobalLoading ? styles.overlayLoading : styles.overlay}
      onMouseDown={handleMouseDown}
      onClick={handleOverlayClick}
    >
      <div ref={contentRef} className={styles.content} onClick={(e) => e.stopPropagation()}>
        {!state.isLoading && state.step !== STEP_CONFIRMATION && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label={t('dashboard.card.activateModal.closeLabel')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {state.isLoading && (
          <div className={styles.loadingPlaceholder} style={{ height: '300px' }} />
        )}

        {/* Step 1: Card Data */}
        {!state.isLoading && state.step === STEP_DATA && (
          <div className={styles.stepContent}>
            <h2 className={styles.title}>{t('dashboard.card.activateModal.step1.title')}</h2>
            <p className={styles.subtitle}>{t('dashboard.card.activateModal.step1.subtitle')}</p>

            <div className={styles.formFields}>
              {hasDisplayFullName(state.fullName) && (
                <>
                  <div className={styles.contextLabel}>
                    {t('dashboard.card.activateModal.step1.contextLabel')}
                  </div>
                  <FormField
                    disabled
                    label={t('dashboard.card.activateModal.step1.fullNameLabel')}
                    value={state.fullName}
                    onChange={handleFullNameChange}
                    error={state.errors['fullName']}
                  />
                </>
              )}
              <FormField
                label={t('dashboard.card.activateModal.step1.cardNumberLabel')}
                value={formatCardNumber(state.cardNumber)}
                onChange={handleCardNumberChange}
                error={state.errors['cardNumber']}
                maxLength={CARD_MAX_LENGTH}
                placeholder={t('dashboard.card.activateModal.step1.cardNumberPlaceholder')}
              />
              <FormField
                label={t('dashboard.card.activateModal.step1.confirmCardNumberLabel')}
                value={formatCardNumber(state.confirmCardNumber)}
                onChange={handleConfirmCardNumberChange}
                error={state.errors['confirmCardNumber']}
                maxLength={CARD_MAX_LENGTH}
                placeholder={t('dashboard.card.activateModal.step1.cardNumberPlaceholder')}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
              />
            </div>

            <div className={styles.actions}>
              <Button
                variant="primary"
                size="md"
                onClick={handleNext}
                disabled={!canGoNext}
                fullWidth
              >
                {t('dashboard.card.activateModal.step1.nextButton')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Manage Limit */}
        {!state.isLoading && state.step === STEP_LIMIT && (
          <div className={styles.stepContent}>
            <h2 className={styles.title}>{t('dashboard.card.activateModal.step2.title')}</h2>
            <p className={styles.subtitle}>
              {t('dashboard.card.activateModal.step2.subtitle', { lastFour: lastFourDigits(state.cardNumber) })}
            </p>

            <div className={styles.formFields}>
              <FormField
                label={t('dashboard.card.activateModal.step2.spendingLimitLabel')}
                value={state.isEditingLimit ? formatCurrencyInput(state.spendingLimit) : state.spendingLimitDisplay}
                onChange={handleSpendingLimitChange}
                onFocus={handleSpendingLimitFocus}
                onBlur={handleSpendingLimitBlur}
                error={state.errors['spendingLimit']}
              />
            </div>

            <div className={styles.actions}>
              <Button
                variant="primary"
                size="md"
                onClick={openMfa}
                disabled={!canActivate}
                fullWidth
              >
                {t('dashboard.card.activateModal.step2.activateButton')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {!state.isLoading && state.step === STEP_CONFIRMATION && (
          <div className={styles.stepContent}>
            <h2 className={styles.title}>
              {state.isPendingApproval
                ? t('dashboard.card.activateModal.step3.pendingTitle')
                : t('dashboard.card.activateModal.step3.title')}
            </h2>
            <p className={styles.confirmationText}>
              {state.isPendingApproval
                ? t('dashboard.card.activateModal.step3.pendingDescription')
                : t('dashboard.card.activateModal.step3.description')}
            </p>

            <div className={styles.actions}>
              <Button variant="primary" size="md" onClick={handleSuccess} fullWidth>
                {t('dashboard.card.activateModal.step3.confirmButton')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Failure */}
        {!state.isLoading && state.step === STEP_FAILED && (
          <div className={styles.stepContent}>
            <h2 className={styles.title}>
              {t('dashboard.card.activateModal.failure.title')}
            </h2>
            <p className={styles.confirmationText}>
              {t('dashboard.card.activateModal.failure.description')}
            </p>

            <div className={styles.actions}>
              <Button variant="primary" size="md" onClick={handleClose} fullWidth>
                {t('dashboard.card.activateModal.failure.button')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {modalContent}
      <MfaModal
        isOpen={isMfaOpen}
        onClose={() => setIsMfaOpen(false)}
        onVerify={handleVerifyMfa}
        isLoading={state.isLoading || isGlobalLoading}
      />
    </>
  );
}
